import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";

const inputCls = "mt-1.5 h-12 w-full rounded-xl border border-slate-200 bg-white px-4 text-base text-navy outline-none focus:border-brand focus:ring-4 focus:ring-brand/15";

export const CodeLoginForm = ({ remember }) => {
  const { t } = useI18n();
  const { loginWithCode } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (!sent) {
        await api.post("/auth/login-code/request", { email });
        setSent(true);
      } else {
        await loginWithCode(email, code, remember);
        navigate("/", { replace: true });
      }
    } catch (err) {
      const s = err.response?.status;
      setError(err.response?.data?.detail === "customer_inactive" ? t("cstat.loginInactive") : s === 401 ? t("auth.codeInvalid") : s === 429 ? t("login.locked") : s === 422 ? t("auth.codeLabel") : t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-6 space-y-4" data-testid="code-login-form">
      <label className="block">
        <span className="text-sm font-medium text-navy">{t("login.email")}</span>
        <input type="email" required value={email} disabled={sent} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="code-login-email" />
      </label>
      {sent && (
        <label className="block">
          <span className="text-sm font-medium text-navy">{t("auth.codeLabel")}</span>
          <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className={`${inputCls} text-center text-2xl tracking-[0.5em]`} data-testid="code-login-code" />
          <span className="mt-1 block text-xs text-slate-500">{t("auth.codeSent")}</span>
          <button type="button" onClick={() => { setSent(false); setCode(""); setError(""); }} className="mt-1 min-h-11 text-sm font-medium text-brand" data-testid="code-login-change-email">{t("auth.changeEmail")}</button>
        </label>
      )}
      {error && <p role="alert" className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="code-login-error">{error}</p>}
      <button type="submit" disabled={busy} data-testid="code-login-submit" className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand text-base font-semibold text-white transition-colors hover:bg-[#25a065] disabled:opacity-60">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {sent ? t("login.submit") : t("auth.sendCode")}
      </button>
    </form>
  );
};
